// The claim controller (bead hf-e6-commitments-coverage-udt.4.2).
//
// One small state machine over "I can help": idle → submitting → settled. It knows
// nothing about Convex; it is handed a function that performs the claim and returns
// the contract's `AcceptNeedResult` — which is exactly what the claim guard produces
// (convex/domain/claimGuard.ts, bead .4.1).
//
// WHY THE PLAN IS REFRESHED ON A REFUSAL, NOT ONLY ON SUCCESS. Losing the race means
// the person is looking at a screen that is now wrong: it says "Needs someone" and
// the need is covered. Leaving that on screen invites a second tap and a second
// refusal, which is how a calm moment turns into a frustrating one. So `onPlanChanged`
// fires whenever the plan is known to have moved — a win OR a race loss.
//
// Under a live Convex query the surrounding view updates on its own, and this callback
// is redundant. It exists anyway, for two reasons: the fixture-backed surfaces have no
// reactivity, and "the view refreshes" is a requirement, so it needs somewhere it can
// actually be observed and tested rather than assumed.

import type { CommitmentRejection, commitments as contracts } from "@herefor/contracts";
import { useCallback, useRef, useState } from "react";

import { isRaceLoss } from "./claimCopy";

export type ClaimResultLike = contracts.AcceptNeedResult;

/** What the screen is doing right now. */
export type ClaimState =
  | { readonly status: "idle" }
  /** In flight. The button is disabled from here, so a double tap cannot be sent. */
  | { readonly status: "submitting" }
  | { readonly status: "accepted"; readonly value: contracts.CommitmentAccepted }
  | { readonly status: "refused"; readonly rejection: CommitmentRejection };

export interface UseClaimOptions {
  /** Performs the claim. In production this calls the boundary-wrapped mutation. */
  readonly claim: () => Promise<ClaimResultLike>;
  /**
   * Called when the plan has demonstrably moved: this person took a place, or
   * somebody else did. Not called for a stale-revision or permission refusal, where
   * nothing about the plan is newly known.
   */
  readonly onPlanChanged?: () => void;
}

export interface ClaimController {
  readonly state: ClaimState;
  /** Offer to help. Safe to call twice: the second call while in flight is ignored. */
  readonly submit: () => Promise<void>;
  /** Return to idle — dismissing the message, not undoing anything. */
  readonly reset: () => void;
}

export function useClaim({ claim, onPlanChanged }: UseClaimOptions): ClaimController {
  const [state, setState] = useState<ClaimState>({ status: "idle" });
  /**
   * In-flight guard, in a ref rather than in the state.
   *
   * A disabled button is not enough: a keyboard repeat or a second tap can be
   * dispatched before React has re-rendered, and state read during that window is
   * still the old state. A ref is updated synchronously, so the second call sees it.
   * This is the double-tap the claim guard would otherwise have to refuse as
   * ALREADY_COMMITTED — better never to send it.
   */
  const inFlight = useRef(false);

  const submit = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setState({ status: "submitting" });

    let result: ClaimResultLike;
    try {
      result = await claim();
    } catch {
      inFlight.current = false;
      // A thrown error is a transport failure, not a domain answer. It is reported
      // through the same panel so there is one place a person looks, and it fails
      // CLOSED: nothing is optimistically shown as agreed.
      setState({
        status: "refused",
        rejection: { kind: "validation_failed", issues: [] },
      });
      return;
    }

    inFlight.current = false;

    if (result.ok) {
      setState({ status: "accepted", value: result.value });
      onPlanChanged?.();
      return;
    }

    setState({ status: "refused", rejection: result.rejection });
    if (isRaceLoss(result.rejection)) onPlanChanged?.();
  }, [claim, onPlanChanged]);

  const reset = useCallback(() => {
    inFlight.current = false;
    setState({ status: "idle" });
  }, []);

  return { state, submit, reset };
}
